/**
 * SandCircle class - manages a canvas with sand particles
 * Ported from original working implementation
 */
import { Particle } from './Particle.js';

export class SandCircle {
    constructor(canvas, particleCount, color, uniformSize, poolId) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d', { alpha: false });
        this.particles = [];
        this.particleCount = particleCount;
        this.color = color;
        this.uniformSize = uniformSize;
        this.poolId = poolId;
        this.isAnimating = true;
        this.isPaused = false;
        this.mouseActive = false;
        this.frameCount = 0;
        this.collisionOffset = 0;
        
        // Set canvas size
        this.resize();
        
        // Initialize particles
        this.initParticles();
        
        // Setup event listeners
        this.setupEventListeners();
        
        // Start animation
        this.animate();
    }

    resize() {
        const rect = this.canvas.getBoundingClientRect();
        this.canvas.width = rect.width;
        this.canvas.height = rect.height;
        this.centerX = this.canvas.width / 2;
        this.centerY = this.canvas.height / 2;
        this.circleRadius = Math.min(this.canvas.width, this.canvas.height) / 2 - 10;
    }

    initParticles() {
        this.particles = [];
        for (let i = 0; i < this.particleCount; i++) {
            const angle = Math.random() * Math.PI * 2;
            const distance = Math.random() * (this.circleRadius - 10);
            const x = this.centerX + Math.cos(angle) * distance;
            const y = this.centerY + Math.sin(angle) * distance;
            
            const particleSize = this.uniformSize ? 2 : (Math.random() * 2.5 + 1);
            const colorVariation = Math.floor(Math.random() * 30) - 15;
            const particleColor = this.adjustColor(this.color, colorVariation);
            
            this.particles.push(new Particle(x, y, particleSize, particleColor));
        }
    }

    adjustColor(color, amount) {
        const hex = color.replace('#', '');
        const r = Math.max(0, Math.min(255, parseInt(hex.substr(0, 2), 16) + amount));
        const g = Math.max(0, Math.min(255, parseInt(hex.substr(2, 2), 16) + amount));
        const b = Math.max(0, Math.min(255, parseInt(hex.substr(4, 2), 16) + amount));
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    }

    setupEventListeners() {
        let lastMouseMoveTime = 0;
        this.canvas.addEventListener('mousemove', (e) => {
            const now = Date.now();
            if (now - lastMouseMoveTime > 16) {
                this.handleMouseMove(e);
                lastMouseMoveTime = now;
            }
        });
        this.canvas.addEventListener('mouseenter', () => { this.mouseActive = true; });
        this.canvas.addEventListener('mouseleave', () => { this.mouseActive = false; });
    }

    handleMouseMove(e) {
        if (this.isPaused) return;
        
        this.mouseActive = true;
        
        const rect = this.canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        
        const isInsuredPool = this.poolId === 4 || this.poolId === 5;
        
        this.particles.forEach(particle => {
            const dx = particle.x - mouseX;
            const dy = particle.y - mouseY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            if (distance < 60 && distance > 0) {
                const force = (60 - distance) / 60;
                particle.vx += (dx / distance) * force * 3;
                particle.vy += (dy / distance) * force * 3;
                
                if (isInsuredPool) {
                    particle.applyClaim(force * 0.3);
                }
            }
        });
    }

    update() {
        if (this.isPaused) return;
        
        // Update each particle
        for (let i = 0; i < this.particles.length; i++) {
            this.particles[i].update(this.circleRadius, this.centerX, this.centerY);
            this.particles[i].updateClaimColor();
        }
        
        // Optimized collision detection
        const step = 5;
        this.collisionOffset = (this.collisionOffset || 0) + 1;
        if (this.collisionOffset >= step) this.collisionOffset = 0;
        
        this.frameCount++;
        if (this.frameCount % 2 === 0) return;
        
        for (let i = this.collisionOffset; i < this.particles.length; i += step) {
            for (let j = i + 1; j < this.particles.length; j++) {
                const dx = this.particles[i].x - this.particles[j].x;
                const dy = this.particles[i].y - this.particles[j].y;
                const maxDist = this.particles[i].radius + this.particles[j].radius;
                
                if (Math.abs(dx) > maxDist || Math.abs(dy) > maxDist) continue;
                
                if (this.particles[i].collidesWith(this.particles[j])) {
                    this.particles[i].resolveCollision(this.particles[j]);
                }
            }
        }
    }

    draw() {
        // Clear canvas
        this.ctx.fillStyle = '#0a0a0a';
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        
        // Batch draw by color
        const colorGroups = {};
        for (let i = 0; i < this.particles.length; i++) {
            const particle = this.particles[i];
            if (!colorGroups[particle.color]) {
                colorGroups[particle.color] = [];
            }
            colorGroups[particle.color].push(particle);
        }
        
        for (const color in colorGroups) {
            this.ctx.fillStyle = color;
            this.ctx.beginPath();
            for (const particle of colorGroups[color]) {
                this.ctx.moveTo(particle.x + particle.radius, particle.y);
                this.ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
            }
            this.ctx.fill();
        }
    }

    animate() {
        if (!this.isAnimating) return;
        
        this.update();
        this.draw();
        
        requestAnimationFrame(() => this.animate());
    }

    pause() {
        this.isPaused = true;
    }

    resume() {
        this.isPaused = false;
    }

    stop() {
        this.isAnimating = false;
    }
}

